import { createContext, useContext, useEffect, useRef, useState } from "react";
import { useSettings } from "./SettingsContext";
import { useAuth } from "./AuthContext";

const ThemeContext = createContext({ applyPalette: () => {}, resetTheme: () => {}, previewing: false });

// #RGB / #RRGGBB -> "h s% l%" (space-separated, ready for hsl(var(--x)))
function hexToHslTriplet(hex) {
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.slice(0, 2), 16) / 255;
  const g = parseInt(h.slice(2, 4), 16) / 255;
  const b = parseInt(h.slice(4, 6), 16) / 255;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  let hue = 0, sat = 0;
  const l = (max + min) / 2;
  if (max !== min) {
    const d = max - min;
    sat = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    if (max === r) hue = (g - b) / d + (g < b ? 6 : 0);
    else if (max === g) hue = (b - r) / d + 2;
    else hue = (r - g) / d + 4;
    hue /= 6;
  }
  return `${Math.round(hue * 360)} ${Math.round(sat * 100)}% ${Math.round(l * 100)}%`;
}

function lightness(hex) {
  let h = hex.replace("#", "");
  if (h.length === 3) h = h.split("").map((c) => c + c).join("");
  const r = parseInt(h.slice(0, 2), 16), g = parseInt(h.slice(2, 4), 16), b = parseInt(h.slice(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255;
}

export function paletteIsLight(p) {
  return !!(p && p.base) && lightness(p.base) > 0.5;
}

export function injectPalette(p) {
  if (!p || !p.base) return;
  const root = document.documentElement;
  const set = (k, v) => root.style.setProperty(k, v);
  // Direct hex tokens
  set("--fx-base", p.base); set("--fx-s1", p.s1); set("--fx-s2", p.s2); set("--fx-s3", p.s3);
  set("--brandblue", p.brandblue); set("--pos", p.pos); set("--neg", p.neg); set("--gold", p.gold);
  set("--body-bg", p.base); set("--body-fg", p.fg); set("--text-muted", p.muted);
  // Derived shadcn HSL tokens (kept in sync with the brand palette)
  const H = hexToHslTriplet;
  set("--background", H(p.base));
  set("--foreground", H(p.fg));
  set("--card", H(p.s1)); set("--card-foreground", H(p.fg));
  set("--popover", H(p.s2)); set("--popover-foreground", H(p.fg));
  set("--primary", H(p.brandblue)); set("--primary-foreground", "0 0% 100%");
  set("--secondary", H(p.s2)); set("--secondary-foreground", H(p.fg));
  set("--muted", H(p.s2)); set("--muted-foreground", H(p.muted));
  set("--accent", H(p.s3)); set("--accent-foreground", H(p.fg));
  set("--destructive", H(p.neg)); set("--destructive-foreground", "0 0% 100%");
  set("--border", H(p.s3)); set("--input", H(p.s3)); set("--ring", H(p.brandblue));
  // Native form controls / scrollbars follow the surface brightness
  const scheme = lightness(p.base) > 0.5 ? "light" : "dark";
  root.style.colorScheme = scheme;
}

export function ThemeProvider({ children }) {
  const { theme, themes } = useSettings();
  const { user } = useAuth();
  const [previewing, setPreviewing] = useState(false);
  const savedRef = useRef(null);

  // Effective palette = the logged-in user's personal preference (if any),
  // otherwise the platform-wide active theme set by admins.
  useEffect(() => {
    const pref = user && typeof user === "object" ? user.theme_preference : null;
    const prefPalette = pref && (themes || []).find((t) => t.id === pref)?.palette;
    const effective = prefPalette || (theme && theme.palette);
    if (effective) {
      savedRef.current = effective;
      if (!previewing) injectPalette(effective);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [theme, themes, user]);

  const applyPalette = (palette) => { setPreviewing(true); injectPalette(palette); };
  const resetTheme = () => { setPreviewing(false); if (savedRef.current) injectPalette(savedRef.current); };
  const commitPalette = (palette) => { savedRef.current = palette; setPreviewing(false); injectPalette(palette); };

  return (
    <ThemeContext.Provider value={{ applyPalette, resetTheme, commitPalette, previewing }}>
      {children}
    </ThemeContext.Provider>
  );
}

export const useTheme = () => useContext(ThemeContext);
